(() => {
  if (customElements.get('hbr-live-feed')) return;
  const subscribers = new Set();
  let pending = null, data = null, generation = 0;
  const sectionID = 'hbr-live-reviews-data';
  // Reuse the product gallery's installed PhotoSwipe assets, only on photo click.
  const assetBase = document.currentScript?.src;
  let photoModule = null, photoStyles = null, activePhoto = null;

  function loadPhotoViewer() {
    if (!assetBase) return Promise.reject(new Error('Image viewer unavailable'));
    if (!photoModule) {
      photoModule = import(new URL('hbr-photoswipe.esm.min.js', assetBase).href)
        .catch(error => { photoModule = null; throw error; });
    }
    if (!photoStyles) {
      photoStyles = new Promise((resolve, reject) => {
        let link = [...document.querySelectorAll('link[rel="stylesheet"]')]
          .find(node => new URL(node.href, location.href).pathname.endsWith('/hbr-photoswipe.css'));
        if (link?.sheet) { resolve(); return; }
        const owned = !link;
        if (!link) {
          link = document.createElement('link'); link.rel = 'stylesheet';
          link.href = new URL('hbr-photoswipe.css', assetBase).href;
        }
        const finish = error => {
          clearTimeout(timer);
          link.removeEventListener('load', loaded); link.removeEventListener('error', failed);
          if (error) { if (owned) link.remove(); reject(error); } else resolve();
        };
        const loaded = () => finish();
        const failed = () => finish(new Error('Image viewer styles unavailable'));
        const timer = setTimeout(failed, 12000);
        link.addEventListener('load', loaded); link.addEventListener('error', failed);
        if (owned) document.head.prepend(link);
      }).catch(error => { photoStyles = null; throw error; });
    }
    return Promise.all([photoModule, photoStyles]).then(([module]) => module.default);
  }

  function openReviewPhoto(photo, row, trigger) {
    if (activePhoto) return;
    const dialog = element('dialog', 'hbr-review-photo');
    // Older browsers retain an ordinary image link as a fallback.
    if (typeof dialog.showModal !== 'function') return false;
    dialog.setAttribute('aria-label', `Customer photo of ${row.product_title}`);
    const panel = element('div', 'hbr-review-photo__panel');
    const close = element('button', 'hbr-review-photo__close', '×');
    close.type = 'button'; close.setAttribute('aria-label', 'Close customer photo');
    const image = element('img', 'hbr-review-photo__image');
    image.alt = `Customer photo of ${row.product_title}`; image.src = photo.thumbnail;
    const caption = element('div', 'hbr-review-photo__caption');
    caption.append(element('strong', '', `Shared by ${row.name}`), element('span', '', row.product_title));
    const status = element('p', 'hbr-review-photo__status', 'Loading photo…');
    status.setAttribute('role', 'status');
    const retry = element('button', 'hbr-review-photo__retry', 'Try again');
    retry.type = 'button'; retry.hidden = true;
    panel.append(close, image, caption, status, retry); dialog.append(panel);
    document.body.append(dialog);

    const x = window.scrollX, y = window.scrollY;
    const body = document.body;
    const changes = { position: 'fixed', top: `${-y}px`, left: `${-x}px`, width: '100%', overflow: 'hidden' };
    const saved = Object.keys(changes).map(key => [key, body.style.getPropertyValue(key), body.style.getPropertyPriority(key)]);
    Object.entries(changes).forEach(([key, value]) => body.style.setProperty(key, value));
    let viewer = null, disposed = false, attempt = 0, cancelImage = null;
    function cleanup() {
      if (disposed) return;
      disposed = true; attempt++; cancelImage?.();
      saved.forEach(([key, value, priority]) => value ? body.style.setProperty(key, value, priority) : body.style.removeProperty(key));
      const html = document.documentElement;
      const behavior = [html.style.getPropertyValue('scroll-behavior'), html.style.getPropertyPriority('scroll-behavior')];
      html.style.setProperty('scroll-behavior', 'auto', 'important');
      if (dialog.open) dialog.close();
      dialog.remove(); window.scrollTo(x, y);
      if (trigger.isConnected) trigger.focus({ preventScroll: true });
      if (behavior[0]) html.style.setProperty('scroll-behavior', ...behavior);
      else html.style.removeProperty('scroll-behavior');
      activePhoto = null;
    }
    const dismiss = () => viewer ? viewer.close() : cleanup();
    close.addEventListener('click', dismiss);
    dialog.addEventListener('cancel', event => { event.preventDefault(); dismiss(); });
    dialog.addEventListener('close', () => { if (!disposed) { viewer?.destroy(); cleanup(); } });
    // A press on the image must never count as a backdrop click.
    let backdropPress = false;
    dialog.addEventListener('pointerdown', event => { backdropPress = event.target === dialog || event.target === panel; });
    dialog.addEventListener('click', event => {
      if (backdropPress && (event.target === dialog || event.target === panel)) dismiss();
      backdropPress = false;
    });
    activePhoto = { destroy: () => { viewer?.destroy(); cleanup(); }, trigger };
    try { dialog.showModal(); } catch (_) { cleanup(); return false; }
    close.focus({ preventScroll: true });

    async function showPhoto() {
      const current = ++attempt;
      status.textContent = 'Loading photo…'; retry.hidden = true;
      const fullImage = new Image();
      const size = new Promise((resolve, reject) => {
        const finish = error => {
          clearTimeout(timer); fullImage.onload = fullImage.onerror = null;
          cancelImage = null;
          if (error) reject(error);
          else resolve({ width: fullImage.naturalWidth, height: fullImage.naturalHeight });
        };
        const timer = setTimeout(() => finish(new Error('Photo timed out')), 15000);
        fullImage.onload = () => fullImage.naturalWidth > 0 ? finish() : finish(new Error('Photo unavailable'));
        fullImage.onerror = () => finish(new Error('Photo unavailable'));
        cancelImage = () => finish(new Error('Photo closed'));
        fullImage.src = photo.url;
      });
      // Keep the full-size image in the dialog if the optional zoom module fails.
      const module = loadPhotoViewer().catch(() => null);
      try {
        const dimensions = await size;
        if (disposed || current !== attempt) return;
        image.src = photo.url; status.textContent = '';
        const PhotoSwipe = await module;
        if (disposed || current !== attempt || !PhotoSwipe) return;
        const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        viewer = new PhotoSwipe({
          dataSource: [{ src: photo.url, width: dimensions.width, height: dimensions.height, alt: image.alt }],
          appendToEl: dialog, mainClass: 'hbr-review-lightbox',
          showHideAnimationType: reducedMotion ? 'none' : 'fade',
          showAnimationDuration: 180, hideAnimationDuration: 180,
          bgOpacity: 1, returnFocus: false, trapFocus: true,
          closeTitle: 'Close customer photo', zoomTitle: 'Zoom customer photo',
          counter: false, arrowPrev: false, arrowNext: false,
          clickToCloseNonZoomable: false, imageClickAction: 'zoom',
          tapAction: false, doubleTapAction: 'zoom',
          paddingFn: viewport => ({ top: viewport.x < 600 ? 80 : 88, bottom: 112, left: 16, right: 16 }),
          errorMsg: 'This photo could not be loaded. Please close it and try again.'
        });
        viewer.on('uiRegister', () => {
          viewer.ui.registerElement({ name: 'review-caption', className: 'hbr-review-lightbox__caption', appendTo: 'root',
            onInit: node => { node.append(caption.cloneNode(true)); } });
        });
        viewer.on('destroy', cleanup);
        viewer.on('afterInit', () => {
          panel.hidden = true;
          // The outer native dialog already owns the accessible modal semantics.
          viewer.element.setAttribute('role', 'group');
          viewer.element.removeAttribute('aria-modal');
          viewer.element.setAttribute('aria-label', 'Photo viewer');
          viewer.element.querySelector('.pswp__button--close')?.focus({ preventScroll: true });
        });
        viewer.init();
      } catch (_) {
        if (disposed || current !== attempt) return;
        if (viewer) { viewer.destroy(); return; }
        status.textContent = 'The larger photo couldn’t load. Please try again.';
        retry.hidden = false;
      }
    }
    retry.addEventListener('click', showPhoto);
    showPhoto();
    return true;
  }

  function photoLink(photo, row) {
    const link = element('a', 'hbr-review-photo-link');
    link.href = photo.url;
    link.setAttribute('aria-label', `View customer photo of ${row.product_title}`);
    link.setAttribute('aria-haspopup', 'dialog');
    link.addEventListener('click', event => {
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return;
      if (openReviewPhoto(photo, row, link) !== false) event.preventDefault();
    });
    return link;
  }
  window.addEventListener('pagehide', () => activePhoto?.destroy());
  document.addEventListener('shopify:section:unload', event => {
    if (activePhoto && event.target.contains(activePhoto.trigger)) activePhoto.destroy();
  });

  function baseURL() {
    const url = new URL(window.Shopify?.routes?.root || '/', location.origin);
    const preview = new URL(location.href).searchParams.get('preview_theme_id');
    if (preview) url.searchParams.set('preview_theme_id', preview);
    url.searchParams.set('section_id', sectionID);
    return url;
  }
  async function fetchPage(number, parameter = 'page') {
    const url = baseURL();
    url.searchParams.set(parameter, String(number));
    const abort = new AbortController();
    const timeout = setTimeout(() => abort.abort(), 15000);
    try {
      const response = await fetch(url, { credentials: 'same-origin', cache: 'no-store', signal: abort.signal });
      if (!response.ok) throw new Error('Review request failed');
      const html = new DOMParser().parseFromString(await response.text(), 'text/html');
      const node = html.querySelector('script[data-hbr-live-review-data]');
      if (!node) throw new Error('Review data section unavailable');
      const payload = JSON.parse(node.textContent);
      if (payload.error || payload.version !== 1 || payload.page !== number ||
          !Number.isInteger(payload.pages) || payload.pages < 1 || payload.pages > 100 ||
          !Array.isArray(payload.reviews) || payload.reviews.length > 250 ||
          typeof payload.page_param !== 'string' || !/^page(?:_[a-zA-Z0-9]+)?$/.test(payload.page_param)) {
        throw new Error('Incomplete review response');
      }
      return payload;
    } finally { clearTimeout(timeout); }
  }
  function safeImage(value) {
    if (typeof value !== 'string') return null;
    try {
      const url = new URL(value, location.origin);
      return url.protocol === 'https:' ? url.href : null;
    } catch { return null; }
  }
  function normalize(row) {
    if (!row || typeof row.id !== 'string' || !row.id || typeof row.name !== 'string' ||
        typeof row.text !== 'string' || typeof row.product_title !== 'string' ||
        typeof row.product_id !== 'string' || !/^\d+$/.test(row.product_id) ||
        !Number.isInteger(row.rating) || row.rating < 1 || row.rating > 5 ||
        typeof row.date !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(row.date) ||
        typeof row.product_url !== 'string' || !Array.isArray(row.photos)) {
      throw new Error('Invalid review record');
    }
    const date = new Date(row.date + 'T00:00:00Z');
    if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== row.date) throw new Error('Invalid review date');
    const url = new URL(row.product_url, location.origin);
    if (url.origin !== location.origin || !url.pathname.includes('/products/')) throw new Error('Invalid product URL');
    return { ...row, name: row.name === 'Inactive Etsy Member' ? 'Customer' : row.name,
      product_url: url.pathname + url.search, featured: row.featured === true,
      photos: row.photos.map(photo => ({ thumbnail: safeImage(photo.thumbnail), card: safeImage(photo.card), url: safeImage(photo.url) }))
        .filter(photo => photo.url && photo.thumbnail && photo.card) };
  }
  async function fetchAll() {
    const first = await fetchPage(1);
    const pages = [first];
    // Small batches avoid flooding Shopify when the catalogue grows.
    for (let start = 2; start <= first.pages; start += 3) {
      const numbers = Array.from({ length: Math.min(3, first.pages - start + 1) }, (_, i) => start + i);
      const batch = await Promise.all(numbers.map(number => fetchPage(number, first.page_param)));
      if (batch.some(page => page.pages !== first.pages || page.page_param !== first.page_param)) throw new Error('Reviews changed while loading');
      pages.push(...batch);
    }
    const seen = new Set();
    return pages.flatMap(page => page.reviews).map(normalize).map(row => {
      if (seen.has(row.id)) throw new Error('Reviews changed while loading');
      seen.add(row.id);
      return row;
    });
  }
  function load(force = false) {
    if (!force && (pending || data)) return;
    const version = ++generation;
    data = null;
    subscribers.forEach(view => view.loading());
    pending = fetchAll().then(rows => {
      if (version !== generation) return;
      data = rows;
      subscribers.forEach(view => view.render(rows));
    }).catch(() => {
      if (version !== generation) return;
      subscribers.forEach(view => view.failed());
    }).finally(() => { if (version === generation) pending = null; });
  }
  function element(tag, className, text) {
    const el = document.createElement(tag);
    if (className) el.className = className;
    if (text !== undefined) el.textContent = text;
    return el;
  }
  function stats(rows) {
    const sum = rows.reduce((total, row) => total + row.rating, 0);
    return { count: rows.length, average: rows.length ? sum / rows.length : 0 };
  }
  function stars(rating) {
    const node = element('span', 'hbr-rating-stars');
    node.setAttribute('role', 'img');
    node.setAttribute('aria-label', `${rating.toFixed(1)} out of 5 stars`);
    const back = element('span', '', '★★★★★'), fill = element('span', 'hbr-rating-stars__fill', '★★★★★');
    back.setAttribute('aria-hidden', 'true'); fill.setAttribute('aria-hidden', 'true');
    fill.style.width = `${rating * 20}%`;
    node.append(back, fill);
    return node;
  }
  function dateNode(date, className = '') {
    const node = element('time', className);
    node.dateTime = date;
    node.textContent = new Intl.DateTimeFormat(document.documentElement.lang || 'en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' }).format(new Date(date + 'T00:00:00Z'));
    return node;
  }
  function card(row, featured = false, showProduct = false) {
    const node = element('article', featured ? 'hbr-review-card hbr-review-card--imported' : 'hbr-user-review');
    if (featured && row.photos.length) {
      const wrapper = element('div', 'hbr-review-card__image');
      const image = element('img', 'hbr-cover');
      image.src = row.photos[0].card; image.alt = `Customer photo of ${row.product_title}`;
      image.loading = 'lazy'; image.width = 400; image.height = 400;
      const link = photoLink(row.photos[0], row);
      link.append(image); wrapper.append(link); node.append(wrapper);
    }
    const body = featured ? element('div', 'hbr-review-card__body') : node;
    const top = element('div', featured ? 'hbr-stars' : 'hbr-user-review__top');
    top.append(stars(row.rating));
    if (!featured) top.append(dateNode(row.date));
    body.append(top);
    if (featured) {
      const title = element('h3', 'hbr-display hbr-review-card__product');
      const link = element('a', '', row.product_title); link.href = row.product_url;
      title.append(link); body.append(title);
    } else {
      body.append(element('p', 'hbr-user-review__author', row.name));
      if (showProduct) {
        const product = element('p', 'hbr-user-review__product');
        const link = element('a', '', row.product_title);
        link.href = row.product_url;
        product.append(link); body.append(product);
      }
    }
    body.append(element('p', 'hbr-user-review__text' + (row.text.trim() ? '' : ' hbr-user-review__rating-only'), row.text.trim() ? row.text : 'Rating only'));
    if (featured) {
      body.append(element('small', '', row.name), dateNode(row.date, 'hbr-review-card__date'));
      node.append(body);
    } else if (row.photos.length) {
      const photos = element('div', 'hbr-user-review__photos');
      row.photos.forEach(photo => {
        const link = photoLink(photo, row);
        const image = element('img'); image.src = photo.thumbnail; image.alt = `Customer photo of ${row.product_title}`;
        image.width = 100; image.height = 100; image.loading = 'lazy';
        link.append(image); photos.append(link);
      });
      body.append(photos);
    }
    return node;
  }
  class LiveView extends HTMLElement {
    connectedCallback() {
      this.frame = requestAnimationFrame(() => {
        if (!this.isConnected) return;
        this.setup(); subscribers.add(this);
        if (data) this.render(data); else { this.loading(); load(); }
      });
    }
    disconnectedCallback() { cancelAnimationFrame(this.frame); subscribers.delete(this); this.events?.abort(); }
    setup() {
      this.events?.abort(); this.events = new AbortController();
      this.querySelector('[data-review-retry]')?.addEventListener('click', () => load(true), { signal: this.events.signal });
    }
    loading() { this.setAttribute('aria-busy', 'true'); }
    failed() { this.setAttribute('aria-busy', 'false'); }
  }
  class LiveRating extends LiveView {
    loading() { super.loading(); this.hidden = false; this.replaceChildren(element('span', 'hbr-rating-loading', 'Loading reviews…')); }
    failed() {
      super.failed();
      const retry = element('button', 'hbr-rating-retry', 'Retry reviews');
      retry.type = 'button'; retry.addEventListener('click', () => load(true), { once: true });
      this.replaceChildren(retry);
    }
    render(rows) {
      this.setAttribute('aria-busy', 'false');
      const matches = this.dataset.format === 'global' ? rows : rows.filter(row => row.product_id === this.dataset.productId);
      const result = stats(matches);
      this.replaceChildren(); this.hidden = !result.count;
      if (!result.count) return;
      const format = this.dataset.format;
      const wrapper = element(format === 'link' ? 'a' : 'span', format === 'link' ? 'hbr-pdp__review-link' : format === 'card' ? 'hbr-collection-card__rating' : 'hbr-live-rating__value');
      if (format === 'link') wrapper.setAttribute('href', '#' + this.dataset.target);
      let text = `${result.average.toFixed(1)} from ${result.count} reviews`;
      if (format === 'link') text = `${result.average.toFixed(1)} · Read ${result.count} reviews ↓`;
      if (format === 'card') text = `${result.average.toFixed(1)} (${result.count})`;
      if (format === 'global') text = `${result.average.toFixed(1)}/5 from ${result.count} product reviews`;
      wrapper.append(stars(result.average), document.createTextNode(' '), element('span', '', text));
      this.append(wrapper);
    }
  }
  class LiveFeed extends LiveView {
    setup() {
      super.setup();
      this.list = this.querySelector('.hbr-review-feed__list');
      this.status = this.querySelector('[data-review-status]');
      this.tools = this.querySelector('.hbr-review-feed__tools');
      this.nav = this.querySelector('.hbr-review-feed__pagination');
      this.retry = this.querySelector('[data-review-retry]');
      this.filter = this.querySelector('[data-review-filter]');
      this.sort = this.querySelector('[data-review-sort]');
      this.shopWide = this.dataset.scope === 'shop';
      this.productFilter = this.querySelector('[data-review-product]');
      this.photoFilter = this.querySelector('[data-review-photos]');
      this.pageSize = this.shopWide ? 9 : 6;
      this.prev = this.querySelector('[data-review-prev]');
      this.next = this.querySelector('[data-review-next]');
      this.page = 0;
      const options = { signal: this.events.signal };
      [this.filter, this.sort, this.productFilter, this.photoFilter].filter(Boolean).forEach(select => select.addEventListener('change', () => { this.page = 0; this.paint(); }, options));
      this.prev.addEventListener('click', () => { this.page--; this.paint(true); }, options);
      this.next.addEventListener('click', () => { this.page++; this.paint(true); }, options);
    }
    loading() {
      super.loading(); this.list.replaceChildren(); this.tools.hidden = true; this.nav.hidden = true; this.retry.hidden = true;
      this.status.textContent = 'Loading customer reviews…';
    }
    failed() { super.failed(); this.status.textContent = 'Reviews are temporarily unavailable. Please try again.'; this.retry.hidden = false; }
    render(rows) {
      this.setAttribute('aria-busy', 'false'); this.retry.hidden = true;
      this.rows = this.shopWide ? rows : rows.filter(row => row.product_id === this.dataset.productId);
      if (this.productFilter) {
        const selected = this.productFilter.value;
        const products = new Map();
        this.rows.forEach(row => {
          const product = products.get(row.product_id) || { title: row.product_title, count: 0 };
          product.count++; products.set(row.product_id, product);
        });
        const all = element('option', '', 'All products'); all.value = 'all';
        this.productFilter.replaceChildren(all, ...Array.from(products).sort((a, b) => a[1].title.localeCompare(b[1].title)).map(([id, product]) => {
          const option = element('option', '', `${product.title} (${product.count})`); option.value = id; return option;
        }));
        this.productFilter.value = products.has(selected) ? selected : 'all';
      }
      this.filter.querySelector('[value="all"]').textContent = 'All ratings';
      for (let star = 1; star <= 5; star++) this.filter.querySelector(`[value="${star}"]`).textContent = `${star} stars (${this.rows.filter(row => row.rating === star).length})`;
      this.page = 0; this.tools.hidden = !this.rows.length;
      this.paint();
    }
    paint(moveFocus = false) {
      const rows = this.rows.filter(row =>
        (this.filter.value === 'all' || row.rating === Number(this.filter.value)) &&
        (!this.productFilter || this.productFilter.value === 'all' || row.product_id === this.productFilter.value) &&
        (!this.photoFilter || this.photoFilter.value !== 'photos' || row.photos.length > 0)
      );
      rows.sort((a, b) => {
        const date = b.date.localeCompare(a.date) || b.id.localeCompare(a.id);
        return this.sort.value === 'highest' ? b.rating - a.rating || date : this.sort.value === 'lowest' ? a.rating - b.rating || date : date;
      });
      const pages = Math.max(1, Math.ceil(rows.length / this.pageSize));
      this.page = Math.max(0, Math.min(this.page, pages - 1));
      const start = this.page * this.pageSize;
      this.list.replaceChildren(...rows.slice(start, start + this.pageSize).map(row => card(row, false, this.shopWide)));
      this.nav.hidden = rows.length <= this.pageSize;
      this.prev.disabled = this.page === 0; this.next.disabled = this.page === pages - 1;
      this.querySelector('[data-review-page]').textContent = `Page ${this.page + 1} of ${pages}`;
      this.status.textContent = rows.length ? `Showing ${start + 1}–${Math.min(start + this.pageSize, rows.length)} of ${rows.length} reviews` : this.rows.length ? (this.shopWide ? 'No reviews match these filters. Try another product, rating or photo option.' : 'No reviews with this rating.') : (this.shopWide ? 'No product reviews yet. Check back soon.' : 'No reviews for this product yet.');
      if (moveFocus) { this.list.focus({ preventScroll: true }); this.list.scrollIntoView({ block: 'start', behavior: 'auto' }); }
    }
  }
  class LiveFeatured extends LiveView {
    setup() { super.setup(); this.track = this.closest('hbr-review-scroller').querySelector('.hbr-reviews__track'); }
    loading() {
      super.loading(); this.track.querySelectorAll('.hbr-review-card--imported').forEach(node => node.remove());
      this.querySelector('[data-review-status]').textContent = 'Loading more customer reviews…';
      this.querySelector('[data-review-retry]').hidden = true;
    }
    failed() {
      super.failed(); this.querySelector('[data-review-status]').textContent = 'More customer reviews are temporarily unavailable.';
      this.querySelector('[data-review-retry]').hidden = false;
    }
    render(rows) {
      this.setAttribute('aria-busy', 'false');
      this.track.querySelectorAll('.hbr-review-card--imported').forEach(node => node.remove());
      const featured = rows.filter(row => row.featured).sort((a, b) => b.date.localeCompare(a.date));
      this.track.append(...featured.map(row => card(row, true)));
      this.querySelector('[data-review-status]').textContent = '';
      this.querySelector('[data-review-retry]').hidden = true;
      this.dispatchEvent(new CustomEvent('hbr:reviews-updated', { bubbles: true }));
    }
  }
  customElements.define('hbr-live-rating', LiveRating);
  customElements.define('hbr-live-feed', LiveFeed);
  customElements.define('hbr-live-featured', LiveFeatured);
  // Back/forward restoration and theme-editor changes must not retain an old feed.
  window.addEventListener('pageshow', event => { if (event.persisted) load(true); });
  document.addEventListener('shopify:section:load', () => { if (subscribers.size) load(true); });
})();
